import type { TaskRunEvent } from "@mlx/contracts";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import { formatRunDuration } from "@product/lib/run-details-format";
import { buildRunSourceUsage } from "@product/lib/run-source-usage";

import { RECEIVABLES_ASK, RECEIVABLES_REPLY } from "../editorial/bands/receivables-story";
import { ShotFigure } from "./figure";
import { CALDER_NOW, CALDER_TURN_TIMES, calderWorkspace } from "./fixture-calder";
import { formatUpdateCount } from "./fixture-run";
import { jsonResult } from "./replay-run";
import { ShotTurn } from "./turn";
import { ShotAnswerFooter, ShotClosedTrace, ShotWindow } from "./window";

const WORKSPACE = calderWorkspace({ sessionId: "ses_aged_receivables", sessionTitle: "Aged receivables by customer" });

/**
 * The receivables answer on the sample workspace's morning: the board's
 * receivables run, its customers invented (receivables-story.ts). Only the
 * dates move to the workspace's clock, so a question asked a minute ago
 * reads a snapshot synced that morning.
 */
export const RECEIVABLES_ANSWER_TODAY = RECEIVABLES_REPLY.replace(
  "As at 11 August 2026",
  "As at 24 September 2026",
).replace("11 August 2026 at approximately 06:53 UTC", "24 September 2026 at approximately 07:53 UTC");

/** The run's one call: the Xero data the warehouse ingested, queried for the receivables. */
const EVENTS = [
  ["tool.started", { toolCallId: "call_1", toolName: "warehouse_query", pluginSlug: "xero", pluginLabel: "Xero", args: {} }],
  ["tool.completed", { toolCallId: "call_1", toolName: "warehouse_query", pluginSlug: "xero", pluginLabel: "Xero", result: jsonResult({ rows: 16 }) }],
].map(([kind, payload], index) => ({
  id: `evt_receivables_${index + 1}`,
  runId: "run_receivables",
  seq: index + 1,
  createdAt: CALDER_NOW - 60_000 + index * 1_000,
  kind,
  payload,
})) as TaskRunEvent[];

const PLUGINS: PluginIconPlugin[] = [
  { slug: "xero", label: "Xero", branding: { iconUrl: "/concepts/product-shot/icons/sources/xero.svg" } },
];

/** The line under the receivables frame, and what the frame shows to assistive technology. */
export const RECEIVABLES_FIGURE = {
  caption: "Aged receivables by customer, with the basis and the sources behind the answer.",
  summary:
    "A finished session in a sample workspace: a services firm asks for its aged receivables by customer. The answer ranks each customer's balance by age, gives the total overdue and the two accounts to chase first, states the basis of the calculation and lists the sources it was retrieved with.",
};

/**
 * The receivables session's window alone, for a section that stages the
 * frame itself: landing-model-choice lays the composer's model menu over it.
 */
export function ReceivablesWindow({ className }: { className?: string }) {
  return (
    <ShotWindow
      tone="light"
      workspace={WORKSPACE}
      chromeInert
      anchor="end"
      className={className}
      transcript={
        <ShotTurn
          question={RECEIVABLES_ASK}
          answerMarkdown={RECEIVABLES_ANSWER_TODAY}
          sources={buildRunSourceUsage(EVENTS)}
          plugins={PLUGINS}
          user={WORKSPACE.user}
          times={CALDER_TURN_TIMES}
        />
      }
      footer={
        <ShotAnswerFooter
          trace={<ShotClosedTrace updateCountLabel={formatUpdateCount(44)} durationLabel={formatRunDuration(128_219)} />}
        />
      }
    />
  );
}

/**
 * A finished session in the sample workspace: the question, the governed
 * answer with its ageing table, the conclusions and basis, and the sources
 * it was retrieved with. It rests where the application leaves a session it
 * opens, at the end of the answer; the transcript scrolls to the table.
 */
export function ReceivablesShot() {
  return (
    <ShotFigure {...RECEIVABLES_FIGURE}>
      <ReceivablesWindow />
    </ShotFigure>
  );
}
